<template>
  <Teleport to="body">
    <Transition name="bottom-drawer">
      <div v-if="modelValue" class="bottom-drawer-overlay" @click="close">
        <div class="bottom-drawer" @click.stop>
          <div class="bottom-drawer__handle" @touchstart="onTouchStart" @touchmove="onTouchMove" @touchend="onTouchEnd">
            <div class="bottom-drawer__handle-bar"></div>
          </div>
          <div v-if="title" class="bottom-drawer__header">
            <h3 class="bottom-drawer__title">{{ title }}</h3>
          </div>
          <div class="bottom-drawer__body">
            <slot>
              <button
                v-for="item in items"
                :key="item.value"
                class="bottom-drawer__item"
                :class="{
                  'bottom-drawer__item--active': item.value === activeValue,
                  'bottom-drawer__item--current': item.value === currentValue,
                }"
                @click="selectItem(item)"
              >
                <span
                  v-if="item.color"
                  class="bottom-drawer__item-dot"
                  :style="{ backgroundColor: item.color }"
                ></span>
                <span class="bottom-drawer__item-label">{{ item.label }}</span>
                <span v-if="item.value === currentValue" class="bottom-drawer__item-badge">
                  {{ currentLabel }}
                </span>
                <BaseIcon
                  v-if="item.value === activeValue"
                  name="check"
                  size="16"
                  class="bottom-drawer__item-check"
                />
              </button>
            </slot>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
  import { defineComponent, ref } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  export default defineComponent({
    name: 'BottomDrawer',

    components: {
      BaseIcon,
    },

    props: {
      modelValue: {
        type: Boolean,
        default: false,
      },
      title: {
        type: String,
        default: '',
      },
      items: {
        type: Array,
        default: () => [],
      },
      activeValue: {
        type: [String, Number],
        default: null,
      },
      currentValue: {
        type: [String, Number],
        default: null,
      },
      currentLabel: {
        type: String,
        default: 'huidig',
      },
    },

    emits: ['update:modelValue', 'select'],

    setup(props, { emit }) {
      const touchStartY = ref(0);
      const dragging = ref(false);

      const close = () => {
        emit('update:modelValue', false);
      };

      const selectItem = (item) => {
        emit('select', item.value);
        close();
      };

      const onTouchStart = (e) => {
        touchStartY.value = e.touches[0].clientY;
        dragging.value = true;
      };

      const onTouchMove = (e) => {
        if (!dragging.value) return;
        const diff = e.touches[0].clientY - touchStartY.value;
        if (diff > 80) {
          close();
          dragging.value = false;
        }
      };

      const onTouchEnd = () => {
        dragging.value = false;
      };

      return {
        close,
        selectItem,
        onTouchStart,
        onTouchMove,
        onTouchEnd,
      };
    },
  });
</script>

<style scoped lang="scss">
  .bottom-drawer-overlay {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5);
  }

  .bottom-drawer {
    width: 100%;
    max-width: 500px;
    max-height: 70vh;
    background: var(--white, #fff);
    border-radius: 1rem 1rem 0 0;
    display: flex;
    flex-direction: column;
    padding-bottom: env(safe-area-inset-bottom, 0);

    &__handle {
      display: flex;
      justify-content: center;
      padding: 0.75rem 0 0.25rem;
      cursor: grab;
    }

    &__handle-bar {
      width: 36px;
      height: 4px;
      background: var(--slate-300, #cbd5e1);
      border-radius: 2px;
    }

    &__header {
      padding: 0.5rem 1.25rem 0.75rem;
    }

    &__title {
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
      color: var(--slate-900, #0f172a);
    }

    &__body {
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      padding-bottom: 0.5rem;
    }

    &__item {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      width: 100%;
      padding: 0.875rem 1.25rem;
      background: none;
      border: none;
      border-bottom: 1px solid var(--slate-100, #f1f5f9);
      font-size: 0.9375rem;
      font-weight: 500;
      color: var(--slate-700, #334155);
      cursor: pointer;
      transition: background-color 0.15s ease;
      text-align: left;

      &:last-child {
        border-bottom: none;
      }

      &:active {
        background-color: var(--slate-50, #f8fafc);
      }

      &--active {
        color: var(--green-700, #15803d);
      }

      &--current {
        color: var(--brand-600, #2563eb);
      }
    }

    &__item-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    &__item-label {
      flex: 1;
    }

    &__item-badge {
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--brand-600, #2563eb);
      background: var(--brand-50, #eff6ff);
      padding: 0.125rem 0.5rem;
      border-radius: 9999px;
    }

    &__item-check {
      color: var(--green-600, #16a34a);
      flex-shrink: 0;
    }
  }

  // Transitions
  .bottom-drawer-enter-active,
  .bottom-drawer-leave-active {
    transition: opacity 0.2s ease;

    .bottom-drawer {
      transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
    }
  }

  .bottom-drawer-enter-from,
  .bottom-drawer-leave-to {
    opacity: 0;

    .bottom-drawer {
      transform: translateY(100%);
    }
  }
</style>
